import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Skeleton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { toNumber } from 'shared/lib/number/number';
import { DynamicTooltip } from 'shared/ui/Tooltip';

import type { SxProps, Theme } from '@mui/material/styles';
import type { KeyboardEvent, ReactNode } from 'react';

interface ReportMetricProps {
    title: string;
    icon?: ReactNode;

    primary: ReactNode | string;
    primaryColor?: string;
    primarySx?: SxProps<Theme>;
    primaryTooltip?: string | JSX.Element;

    secondary?: ReactNode | string;
    secondarySx?: SxProps<Theme>;

    children?: ReactNode;
    loading?: boolean;
    sx?: SxProps<Theme>;
    minHeight?: number | string;
    onClick?: () => void;
}

const getPrimaryNumber = (
    value: ReportMetricProps['primary'],
): number | null => {
    if (typeof value === 'number') return value;

    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed === '-') return null;

        const numericPattern = /-?\d[\d,]*(?:\.\d+)?/;
        const match = numericPattern.exec(trimmed);
        if (match == null) return null;

        const normalizedNumeric = match[0].replaceAll(',', '');
        return toNumber(normalizedNumeric);
    }

    return null;
};

// I absolutely hate this function and I want to redo how I'm laying out the report metrics... but it's right before we ship
// So we have this terrible solution for now. I am sorry.
const getPrimaryMinWidth = (value: number | null): string => {
    if (value === null || Number.isNaN(value)) {
        return '140px';
    }

    const digitCount = value
        .toString()
        .replaceAll(',', '')
        .replaceAll('.', '').length;

    if (digitCount >= 8) {
        return '250px';
    }
    if (digitCount >= 7) {
        return '220px';
    }
    if (digitCount >= 6) {
        return '200px';
    }
    if (digitCount >= 5) {
        return '190px';
    }
    if (digitCount >= 4) {
        return '160px';
    }

    return '140px';
};

export const ReportMetric = ({
    title,
    icon,
    primary,
    primaryColor,
    primarySx,
    primaryTooltip,
    secondary,
    secondarySx,
    children,
    loading = false,
    sx,
    minHeight = '200px',
    onClick,
}: ReportMetricProps) => {
    const { t } = useTranslation();

    const primaryNumber = getPrimaryNumber(primary);
    const primaryMinWidth = getPrimaryMinWidth(primaryNumber);
    const isZeroPrimary = primaryNumber === 0;
    const headingGrey = 'rgba(0, 0, 0, 0.50)';
    const primaryColorBase = primaryColor ?? 'common.black';
    const primaryColorForDisplay = isZeroPrimary
        ? headingGrey
        : primaryColorBase;

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (!onClick) return;
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onClick();
        }
    };

    return (
        <Box
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            onClick={onClick}
            onKeyDown={handleKeyDown}
            sx={{
                flex: '1 1 0',
                minWidth: primaryMinWidth,
                border: '1px solid #E0E0E0',
                borderRadius: '12px',
                padding: 2,
                backgroundColor: '#FFFFFF',
                minHeight,
                cursor: onClick ? 'pointer' : 'default',
                transition: 'background-color 0.15s ease-out',
                display: 'flex',
                flexDirection: 'column',
                '&:hover': onClick
                    ? {
                          backgroundColor: 'action.hover',
                      }
                    : undefined,
                '&:hover .report-metric-see-details': onClick
                    ? {
                          color: 'primary.dark',
                      }
                    : undefined,
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    marginBottom: '6px',
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        height: '20px',
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            color: headingGrey,
                            fontSize: '13px',
                            lineHeight: '20px',
                            fontWeight: 600,
                        }}
                    >
                        {title}
                    </Typography>
                    {primaryTooltip && (
                        <DynamicTooltip
                            content={primaryTooltip}
                            containerStyle={{ paddingTop: '6px' }}
                        >
                            <InfoOutlinedIcon
                                sx={{
                                    fontSize: 18,
                                    color: '#B7B7B7',
                                }}
                                data-testclass="icon-tooltip"
                            />
                        </DynamicTooltip>
                    )}
                </Box>
                {icon && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: primaryColor ?? 'text.secondary',
                        }}
                    >
                        {icon}
                    </Box>
                )}
            </Box>

            {loading ? (
                <Skeleton
                    variant="text"
                    sx={{
                        fontSize: '38px',
                        lineHeight: '52px',
                        fontWeight: 700,
                        width: '60%',
                        height: '52px',
                    }}
                />
            ) : (
                <Typography
                    data-testclass="report-metric-primary"
                    sx={{
                        fontSize: 'clamp(30px, 2.4vw, 38px)',
                        lineHeight: '52px',
                        fontWeight: 700,
                        color: primaryColorForDisplay,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        ...primarySx,
                    }}
                >
                    {primary}
                </Typography>
            )}

            {!loading && secondary && (
                <Typography
                    variant="body2"
                    data-testclass="report-metric-secondary"
                    sx={{
                        color: 'rgba(0, 0, 0, 0.50)',
                        fontSize: '13px',
                        lineHeight: '20px',
                        ...secondarySx,
                    }}
                >
                    {secondary}
                </Typography>
            )}

            {!loading && children && (
                <Box
                    sx={{
                        marginTop: '16px',
                        paddingTop: '24px',
                        borderTop: '1px solid #E0E0E0',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                    }}
                >
                    {children}
                </Box>
            )}

            {!loading && onClick && (
                <Typography
                    variant="body2"
                    data-testclass="report-metric-see-details"
                    sx={{
                        marginTop: 'auto',
                        fontSize: '13px',
                        lineHeight: '16px',
                        fontWeight: 400,
                        color: 'primary.main',
                    }}
                >
                    {t('reportMetric.seeDetails', 'See details')}
                </Typography>
            )}
        </Box>
    );
};
